import {
  autoPlacement,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFloatingTree,
  useFocus,
  useHover,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStyles,
  useTypeahead,
} from "@floating-ui/react";
import type { RefObject } from "react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useControlledValue } from "../../hooks/useControlledValue";
import { useReturnFocus } from "../../hooks/useReturnFocus";
import { pxToRem } from "../../utils";
import type { IDropdownControlProps } from "./aria";
import { resolveAriaName, splitReferenceProps } from "./aria";
import type { DropdownContextValue, DropdownProps } from "./types";

export type IDropdownViewModel = DropdownContextValue & {
  /** Pointer and positioning props for the trigger box. */
  triggerProps: Record<string, unknown>;
  navigatesWithArrowKeys: boolean;
  /** Element the panel hands focus back to, or null to hand it back to nobody. */
  returnFocusRef: RefObject<HTMLElement | null>;
  /** Id of the element that holds the widget role: the trigger, or its inner control. */
  controlId: string;
  /** Text of the inner control's native `<label>`, when it has one and no explicit name is set. */
  controlLabel: string | undefined;
};

export const useViewModel = (props: DropdownProps): IDropdownViewModel => {
  const {
    trigger,
    ariaLabel,
    ariaLabelledby,
    ariaDescribedby,
    innerControl = false,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    defaultIsOpen,
    disabled = false,
    animation = true,
    placement = "bottom",
    offset: offsetValue = 8,
    flip: flipEnabled = true,
    shift: shiftEnabled = true,
    autoPlacement: autoPlacementEnabled = false,
    interactions = ["click", "dismiss"],
    role = "dialog",
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    matchTriggerWidth = false,
    typeaheadListRef,
    triggerId: propsTriggerId,
    contentId: propsContentId,
    middleware,
    clickOptions,
    hoverOptions,
    focusOptions,
    dismissOptions,
    roleOptions,
    listNavigationOptions,
    typeaheadOptions,
  } = props;

  const generatedId = useId();
  const triggerId = propsTriggerId || `${generatedId}-trigger`;
  const contentId = propsContentId || `${generatedId}-content`;
  const controlId = (trigger.props.id as string | undefined) || triggerId;

  const { value: open, onChange: onOpenChange } = useControlledValue<boolean>({
    controlledValue: controlledOpen,
    controlledOnChange: controlledOnOpenChange,
    defaultValue: defaultIsOpen,
  });

  const tree = useFloatingTree();

  const handleDismiss = useCallback(() => {
    onOpenChange(false);

    if (tree) {
      tree.events.emit("dismissAll");
    }
  }, [onOpenChange, tree]);

  useEffect(() => {
    if (!tree) {
      return;
    }

    const handleDismissAll = () => {
      onOpenChange(false);
    };

    tree.events.on("dismissAll", handleDismissAll);
    return () => {
      tree.events.off("dismissAll", handleDismissAll);
    };
  }, [tree, onOpenChange]);

  const listRef = useRef<(HTMLElement | null)[]>([]);
  const navigationRef = useCallback((index: number) => {
    return (node: HTMLElement | null): void => {
      listRef.current[index] = node;
    };
  }, []);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const {
    refs,
    floatingStyles,
    context,
    placement: currentPlacement,
  } = useFloating({
    open,
    onOpenChange,
    placement,
    middleware:
      middleware ??
      [
        offset(offsetValue),
        flipEnabled && flip(),
        shiftEnabled && shift(),
        autoPlacementEnabled && autoPlacement(),
        size({
          apply({ availableWidth, availableHeight, elements, rects }) {
            const finalMaxWidth = maxWidth
              ? Math.min(maxWidth, availableWidth)
              : availableWidth;
            const finalMaxHeight = maxHeight
              ? Math.min(maxHeight, availableHeight)
              : availableHeight;

            const width = matchTriggerWidth ? rects.reference.width : undefined;

            Object.assign(elements.floating.style, {
              maxWidth: `${finalMaxWidth}px`,
              maxHeight: `${finalMaxHeight}px`,
              ...(minWidth && { minWidth: pxToRem(minWidth) }),
              ...(minHeight && { minHeight: pxToRem(minHeight) }),
              ...(width && { width: `${width}px` }),
            });
          },
        }),
      ].filter(Boolean),
    whileElementsMounted: autoUpdate,
  });

  // Focus goes back to whatever holds the widget role: the inner control, not its box.
  const getReturnFocusSource = useCallback((): Element | null => {
    const reference = refs.domReference.current;
    if (!innerControl || !reference) return reference;
    return reference.querySelector(`[id="${CSS.escape(controlId)}"]`);
  }, [refs.domReference, innerControl, controlId]);

  const returnFocusRef = useReturnFocus(
    context.events,
    !!open,
    getReturnFocusSource,
  );

  // An input named by a native `<label for>` lends that text to the panel.
  // Pointing the panel's `aria-labelledby` at the input would name it by the
  // input's value instead (accname: embedded control).
  const [controlLabel, setControlLabel] = useState<string>();
  useLayoutEffect(() => {
    if (!open || !innerControl || ariaLabel || ariaLabelledby) return;

    const control = getReturnFocusSource();
    const labels =
      control instanceof HTMLInputElement
        ? Array.from(control.labels ?? [])
        : [];
    const text = labels
      .map((label) => label.textContent?.trim())
      .filter(Boolean)
      .join(" ");
    setControlLabel(text || undefined);
  }, [open, innerControl, ariaLabel, ariaLabelledby, getReturnFocusSource]);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: !animation || prefersReducedMotion ? 0 : 100,
    initial: { opacity: 0 },
  });

  const isInteractionEnabled = (interaction: string) => {
    return (interactions as string[]).includes(interaction);
  };

  const click = useClick(context, {
    ...clickOptions,
    enabled:
      clickOptions?.enabled || (isInteractionEnabled("click") && !disabled),
    event: clickOptions?.event || "click",
    toggle: clickOptions?.toggle || true,
  });

  const hover = useHover(context, {
    ...hoverOptions,
    enabled:
      hoverOptions?.enabled || (isInteractionEnabled("hover") && !disabled),
    delay: hoverOptions?.delay,
    handleClose: hoverOptions?.handleClose || safePolygon(),
  });

  const focus = useFocus(context, {
    ...focusOptions,
    enabled:
      focusOptions?.enabled || (isInteractionEnabled("focus") && !disabled),
  });

  const dismiss = useDismiss(context, {
    ...dismissOptions,
    enabled:
      dismissOptions?.enabled || (isInteractionEnabled("dismiss") && !disabled),
    outsidePress:
      dismissOptions?.outsidePress ||
      ((event) => {
        const target = event.target as Node;
        const isOwnDropdown = refs.floating.current?.contains(target);
        const isTrigger = refs.domReference.current?.contains(target);
        return !isOwnDropdown && !isTrigger;
      }),
    escapeKey: dismissOptions?.escapeKey || true,
    bubbles: dismissOptions?.bubbles || {
      escapeKey: false,
    },
  });

  const roleProps = useRole(context, {
    ...roleOptions,
    role: roleOptions?.role || role,
  });

  const listNavigation = useListNavigation(context, {
    ...listNavigationOptions,
    enabled:
      listNavigationOptions?.enabled ||
      (isInteractionEnabled("listNavigation") && !disabled),
    listRef: listNavigationOptions?.listRef || listRef,
    activeIndex: listNavigationOptions?.activeIndex || activeIndex,
    onNavigate: listNavigationOptions?.onNavigate || setActiveIndex,
    loop: listNavigationOptions?.loop || true,
    nested: listNavigationOptions?.nested,
    parentOrientation: listNavigationOptions?.parentOrientation,
    rtl: listNavigationOptions?.rtl,
  });

  const typeahead = useTypeahead(context, {
    ...typeaheadOptions,
    enabled:
      typeaheadOptions?.enabled ||
      (isInteractionEnabled("typeahead") && !disabled),
    listRef: typeaheadOptions?.listRef || typeaheadListRef || { current: [] },
    activeIndex: typeaheadOptions?.activeIndex || activeIndex,
    onMatch: typeaheadOptions?.onMatch || setActiveIndex,
    resetMs: typeaheadOptions?.resetMs || 750,
  });

  const interactionHooks = [
    click,
    hover,
    focus,
    dismiss,
    roleProps,
    listNavigation,
    typeahead,
  ];

  const { getReferenceProps, getFloatingProps, getItemProps } =
    useInteractions(interactionHooks);

  const navigatesWithArrowKeys = isInteractionEnabled("listNavigation");

  // Without `innerControl` the trigger itself is the control and takes every prop.
  const { triggerProps, controlProps } = useMemo(() => {
    const referenceProps = getReferenceProps();

    if (!innerControl) {
      return { triggerProps: referenceProps, controlProps: {} };
    }

    const { anchorProps, controlProps: referenceControlProps } =
      splitReferenceProps(referenceProps);

    const control = referenceControlProps as IDropdownControlProps;

    return {
      triggerProps: anchorProps,
      controlProps: {
        ...control,
        id: controlId,
        tabIndex: disabled ? -1 : (control.tabIndex ?? 0),
        "aria-disabled": disabled || undefined,
        ...resolveAriaName(ariaLabel, ariaLabelledby),
        "aria-describedby": ariaDescribedby,
      },
    };
  }, [
    getReferenceProps,
    innerControl,
    disabled,
    controlId,
    ariaLabel,
    ariaLabelledby,
    ariaDescribedby,
  ]);

  return {
    refs,
    triggerProps,
    controlProps,
    controlId,
    controlLabel,
    returnFocusRef,
    navigatesWithArrowKeys,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps: navigatesWithArrowKeys ? getItemProps : undefined,
    open: !!open,
    setOpen: onOpenChange,
    placement: currentPlacement,
    activeIndex,
    setActiveIndex,
    navigationRef,
    props,
    dismiss: handleDismiss,
    isMounted,
    transitionStyles,
    triggerId,
    contentId,
  };
};
